import { styled } from '@mui/material/styles';
import ResourcesSidebar, {
  RESOURCES_SIDEBAR_WIDTH,
} from '@/components/ResourcesSidebar/ResourcesSidebar';
import { useCallback, useMemo, useState, useRef } from 'react';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import { FormattedMessage } from 'react-intl';
import { Box } from '@mui/material';
import Button from '@/components/Button/Button';
import AddCircleIcon from '@/assets/add-circle.svg?react';
import SearchTextField from '@/components/SearchTextField/SearchTextField';
import Table from '@/components/Table/Table';
import { ColumnDef } from '@/components/Table/TableHelpers';
import IconButton from '@mui/material/IconButton';
import EditPencilIcon from '@/assets/edit-pencil.svg?react';
import { fetchPartners } from '@/lib/api';
import { partnerQueryKeys } from '@/lib/partnerQueryKeys';
import PartnerBadge from '@/components/PartnerBadge/PartnerBadge';
import { PartnerType } from '@/lib/types';
import { useNavigate } from 'react-router';
import CreatePartnerLeadModal from './CreatePartnerLeadModal';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import { NavigationHeight } from '@/components/Navigation/NavigationHelpers';
import { mapFiltersToParams } from '../Resources/ResourcePageHelpers';
import useHasAnyUserRole from '@/hooks/useHasAnyUserRole';
import {
  ScrollContainerContext,
  ScrollContainerRef,
} from '@/contexts/ScrollContainerContext';

const PartnersAdminPage = () => {
  const [createPartnerLeadModalOpen, setCreatePartnerLeadModalOpen] =
    useState(false);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<FilterSchema>();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const hasUserRole = useHasAnyUserRole([
    'Super Admin',
    'Internal Admin',
  ]);

  const filterParams = mapFiltersToParams(
    filters,
    undefined,
    searchText
      ? {
          search: searchText,
        }
      : undefined,
  );
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { data, isFetchingNextPage, refetch } = useInfiniteScroll({
    fetchFn: fetchPartners,
    queryKey: partnerQueryKeys.infinite(filterParams),
    params: filterParams,
    scrollContainerRef:
      scrollContainerRef as React.RefObject<HTMLElement>,
    pageSize: 20,
    scrollThreshold: 200,
  });

  const handleFiltersChange = useCallback(
    (newFilters: FilterSchema) => {
      setFilters(newFilters);
    },
    [],
  );

  const visibleSections = useMemo(
    () => ({
      partnerType: true,
      platform: true,
      operator: true,
      brand: true,
      market: true,
    }),
    [],
  );

  const columns: ColumnDef<Data>[] = [
    {
      field: 'name',
      label: 'Partner Name',
      width: '50%',
    },
    {
      field: 'type',
      label: 'Partner Type',
      render: (_row, value) => (
        <PartnerBadge type={value as PartnerType} />
      ),
      width: '35%',
    },
    {
      id: 'edit',
      label: <FormattedMessage id="edit" defaultMessage="Edit" />,
      width: '10%',
      sortable: false,
      render: (row) => (
        <IconButton
          onClick={() => navigate(`/admin/partners/${row.id}`)}
        >
          <EditPencilIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Root data-sidebar-open={isSidebarOpen}>
      <ResourcesSidebar
        onFiltersChange={handleFiltersChange}
        visibleSections={visibleSections}
        onOpenChange={setIsSidebarOpen}
      />
      <ScrollContainerContext.Provider
        value={scrollContainerRef as unknown as ScrollContainerRef}
      >
        <div
          ref={scrollContainerRef}
          className="admin-page-wrapper wide-scrollbar"
        >
          <div className="admin-content page-width-wrapper">
            <Main>
              <Box
                sx={{ display: 'flex', width: '100%', marginTop: '40px' }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Title>
                    <FormattedMessage
                      id="partnersAdmin"
                      defaultMessage="Partners Admin"
                    />
                  </Title>
                </Box>
                {hasUserRole ? (
                  <Button
                    variant="contained"
                    onClick={() =>
                      setCreatePartnerLeadModalOpen(true)
                    }
                    aria-label="Create Partner Lead Button"
                  >
                    <AddCircleIcon />
                    <FormattedMessage
                      id="createPartnerLead"
                      defaultMessage="Create Partner Lead"
                    />
                  </Button>
                ) : null}
              </Box>
              <StyledSearchTextField
                placeholder={
                  <FormattedMessage
                    id="filterAsYouType"
                    defaultMessage="Filter as you type"
                  />
                }
                value={searchText}
                onChange={setSearchText}
              />
              <StyledTable rows={data as Data[]} columns={columns} />
              {isFetchingNextPage && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <FormattedMessage
                    id="loading"
                    defaultMessage="Loading..."
                  />
                </Box>
              )}
            </Main>
            <CreatePartnerLeadModal
              open={createPartnerLeadModalOpen}
              onClose={() => setCreatePartnerLeadModalOpen(false)}
              onSave={() => {
                refetch();
                setCreatePartnerLeadModalOpen(false);
              }}
            />
          </div>
        </div>
      </ScrollContainerContext.Provider>
    </Root>
  );
};

type Data = {
  id: number;
  name: string;
  type: string;
};

const Root = styled('div')({
  position: 'relative',
  width: '100%',
  background: '#F9FAFB',
  '& .admin-page-wrapper': {
    height: `calc(100vh - ${NavigationHeight}px)`,
    overflowY: 'auto',
    width: '100%',
  },
  '& .admin-content': {
    width: '100%',
    transition: 'padding 0.25s',
    paddingLeft: 0,
  },
  '&[data-sidebar-open="true"] .admin-content': {
    paddingLeft: RESOURCES_SIDEBAR_WIDTH,
  },
});

const Main = styled('div')(({ theme }) => ({
  width: '100%',
  padding: `0 ${theme.spacing(4)}`,
}));

const Title = styled('div')(({ theme }) => ({
  fontSize: '24px',
  lineHeight: '28px',
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
  marginBottom: 8,
}));

const StyledSearchTextField = styled(SearchTextField)({
  maxWidth: 500,
});

const StyledTable = styled(Table<Data>)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

export default PartnersAdminPage;
